import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Moon, Sun, Heart, User, LogOut, Menu as MenuIcon, MapPin, Building2, Info, LogIn, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MuiMenu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { BrandLogo, BrandMark } from "@/components/brand/Logo";
import MobileMenu from "./MobileMenu";

const NAV_LINKS = [
  { label: "Stays", to: "/villas" },
  { label: "Destinations", to: "/#destinations" },
  { label: "Inspiration", to: "/#inspiration" },
  { label: "Why Nirvagoo", to: "/#why-nirvagoo" },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isAdmin = user?.role === "admin";
  const isOwner = user?.role === "owner";
  const isHost = isOwner || isAdmin;

  const isHome = pathname === "/";

  useEffect(() => {
    if (!isHome) return;
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const overlay = isHome && !scrolled;

  return (
    <Box
      component="header"
      data-testid="main-navbar"
      sx={{
        position: isHome ? "fixed" : "sticky", top: 0, left: 0, right: 0, zIndex: 50,
        bgcolor: overlay ? "rgba(0,0,0,0.5)" : "background.default",
        borderBottom: overlay ? "none" : 1, borderColor: "divider",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
      }}
    >
      <Box sx={{ position: "relative", mx: "auto", maxWidth: 1400, px: { xs: 2, sm: 3, lg: 6 }, height: { xs: 68, md: 80 }, display: "flex", alignItems: "center", justifyContent: "space-between", gap: { xs: 1, md: 2 } }}>
        <Box component={Link} to={isAdmin ? "/admin" : isOwner ? "/owner" : "/"} data-testid="nav-logo" sx={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>
          <Box sx={{ display: { xs: "flex", sm: "none" } }}><BrandLogo size={24} tone={overlay ? "white" : undefined} /></Box>
          <Box sx={{ display: { xs: "none", sm: "flex" } }}><BrandLogo size={34} tone={overlay ? "white" : undefined} /></Box>
        </Box>

        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 3.5 }}>
          {NAV_LINKS.map((l) => (
            <Box
              key={l.label}
              component={Link}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              sx={{ fontSize: 14, fontWeight: 500, textDecoration: "none", color: overlay ? "#fff" : "text.primary", "&:hover": { color: overlay ? "#fff" : "primary.main", opacity: overlay ? 0.8 : 1 } }}
            >
              {l.label}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 }, flexShrink: 0 }}>
          <Button
            component={Link}
            to="/owner"
            variant="outlined"
            size="small"
            data-testid="nav-host-link"
            sx={overlay ? {
              display: { xs: "none", md: "inline-flex" }, borderRadius: 999,
              bgcolor: "#fff", color: "#1a1a1a", border: "none",
              "&:hover": { bgcolor: "#fff", opacity: 0.9 },
            } : {
              display: { xs: "none", md: "inline-flex" }, borderRadius: 999,
            }}
          >
            List your villa
          </Button>
          <IconButton
            onClick={toggle} aria-label="Toggle theme" data-testid="theme-toggle"
            sx={{ width: 40, height: 40, color: overlay ? "#fff" : "inherit", "&:hover": { bgcolor: overlay ? alpha("#fff", 0.15) : "action.hover" } }}
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </IconButton>
          <Box
            component="button"
            type="button"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            data-testid="user-menu-btn"
            sx={{
              width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
              border: "none", cursor: "pointer",
              bgcolor: user ? "primary.main" : overlay ? alpha("#fff", 0.2) : "action.hover",
              color: user ? "primary.contrastText" : overlay ? "#fff" : "text.primary",
              fontSize: 13, fontWeight: 500, font: "inherit",
            }}
          >
            {user ? (user.name?.[0]?.toUpperCase() || "U") : <User size={15} />}
          </Box>
          <IconButton
            onClick={() => setMenuOpen(true)} aria-label="Menu" data-testid="hamburger-btn"
            sx={{ width: 40, height: 40, color: overlay ? "#fff" : "inherit", "&:hover": { bgcolor: overlay ? alpha("#fff", 0.15) : "action.hover" } }}
          >
            <MenuIcon size={18} />
          </IconButton>
          <MuiMenu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
            {user && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                <Typography variant="caption" color="text.secondary">{user.email}</Typography>
              </Box>
            )}
            {user && <Divider />}
            {user && isAdmin && (
              <MenuItem component={Link} to="/admin" data-testid="menu-admin" onClick={() => setAnchorEl(null)}>
                <ShieldCheck size={14} style={{ marginRight: 8 }} />Admin Portal
              </MenuItem>
            )}
            {user && isOwner && (
              <MenuItem component={Link} to="/owner" data-testid="menu-owner" onClick={() => setAnchorEl(null)}>
                <Building2 size={14} style={{ marginRight: 8 }} />Owner Portal
              </MenuItem>
            )}
            {user && !isHost && (
              <MenuItem component={Link} to="/dashboard" data-testid="menu-dashboard" onClick={() => setAnchorEl(null)}>
                <User size={14} style={{ marginRight: 8 }} />Dashboard
              </MenuItem>
            )}
            {user && !isHost && (
              <MenuItem component={Link} to="/dashboard?tab=saved" data-testid="menu-saved" onClick={() => setAnchorEl(null)}>
                <Heart size={14} style={{ marginRight: 8 }} />Saved Villas
              </MenuItem>
            )}
            {!user && (
              <MenuItem component={Link} to="/login" data-testid="nav-signin" onClick={() => setAnchorEl(null)}>
                <LogIn size={14} style={{ marginRight: 8 }} />Sign in
              </MenuItem>
            )}
            <Divider />
            <MenuItem component={Link} to="/villas" data-testid="menu-stays" onClick={() => setAnchorEl(null)}>
              <MapPin size={14} style={{ marginRight: 8 }} />Stays
            </MenuItem>
            <MenuItem component={Link} to="/map" data-testid="menu-map" onClick={() => setAnchorEl(null)}>
              <MapPin size={14} style={{ marginRight: 8 }} />Map
            </MenuItem>
            <MenuItem component={Link} to="/p/hosts" data-testid="menu-hosts" onClick={() => setAnchorEl(null)}>
              <Building2 size={14} style={{ marginRight: 8 }} />Hosts
            </MenuItem>
            <MenuItem component={Link} to="/p/about" data-testid="menu-about" onClick={() => setAnchorEl(null)}>
              <Info size={14} style={{ marginRight: 8 }} />About
            </MenuItem>
            {user && <Divider />}
            {user && (
              <MenuItem onClick={() => { setAnchorEl(null); logout(); }} data-testid="menu-logout">
                <LogOut size={14} style={{ marginRight: 8 }} />Sign out
              </MenuItem>
            )}
          </MuiMenu>
        </Box>
      </Box>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </Box>
  );
}
